/**
 * 공정도 아래의 Ontology · LangGraph 패널 (계획서 §13.1, §13.5).
 *
 * 공정도와 하단 탭 사이가 비어 있어 2026-09-04 에 두 그래프를 여기로 올렸다.
 * 탭을 옮겨 다니지 않아도 영향 관계와 workflow 진행이 한눈에 보인다.
 * 하단 탭의 같은 항목은 자세히 보는 자리로 남는다 (§13.5 의 5개 탭 요구).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchOntology, fetchWorkflow } from "@/api/client";

import { GraphErrorBoundary } from "./GraphErrorBoundary";
import { OntologyGraph } from "./OntologyGraph";
import { WorkflowGraph } from "./WorkflowGraph";

export function InsightPanels({ runId }: { runId: string }): React.JSX.Element {
  return (
    <div className="insight-panels">
      <section aria-label="영향 관계">
        <h2>영향 관계 (Ontology)</h2>
        <OntologyPanel runId={runId} />
      </section>
      <section aria-label="Agent workflow">
        <h2>Agent workflow (LangGraph)</h2>
        <WorkflowPanel runId={runId} />
      </section>
    </div>
  );
}

function OntologyPanel({ runId }: { runId: string }): React.JSX.Element {
  const { data, isPending } = useQuery({
    queryKey: ["ontology", runId],
    queryFn: () => fetchOntology(runId),
    refetchInterval: 10_000,
  });

  if (isPending) return <p className="hint">불러오는 중…</p>;
  if (!data?.available) {
    return <p className="hint">Neo4j 를 사용할 수 없어 표시하지 않습니다.</p>;
  }
  if (data.nodes.length === 0) {
    return <p className="hint">고장이 발생하면 영향 관계가 그려집니다.</p>;
  }
  return (
    <GraphErrorBoundary fallback={<p className="hint">그래프를 그리지 못했습니다.</p>}>
      <OntologyGraph nodes={data.nodes} relationships={data.relationships} compact />
    </GraphErrorBoundary>
  );
}

function WorkflowPanel({ runId }: { runId: string }): React.JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isPending } = useQuery({
    queryKey: ["workflow", runId],
    queryFn: () => fetchWorkflow(runId),
    refetchInterval: 5_000,
  });

  if (isPending) return <p className="hint">불러오는 중…</p>;
  if (!data?.available) {
    return <p className="hint">LangGraph checkpointer 를 사용할 수 없어 표시하지 않습니다.</p>;
  }

  return (
    <>
      <p className="hint">
        다음 node {data.next.length > 0 ? data.next.join(", ") : "없음 (완료)"} · 대기 중인 사람
        결정 {data.interrupts.length > 0 ? data.interrupts.map((i) => i.kind).join(", ") : "없음"}
      </p>
      <GraphErrorBoundary fallback={<p className="hint">도식을 그리지 못했습니다.</p>}>
        <WorkflowGraph
          data={data}
          selected={selected}
          onSelect={(node) => {
            setSelected(selected === node ? null : node);
          }}
          compact
        />
      </GraphErrorBoundary>
    </>
  );
}
